import { type ClassValue, clsx } from 'clsx'
import axios, { AxiosError } from 'axios'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Date utilities
export function formatDate(date: string | Date): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date
  return new Intl.DateTimeFormat('es-ES', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(dateObj)
}

export function formatTime(minutes: number): string {
  if (minutes < 60) {
    return `${minutes} min`
  }
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}min` : `${hours}h`
}

export function getWeekStart(date: Date = new Date()): Date {
  const d = new Date(date)
  const day = d.getDay()
  const diff = d.getDate() - day + (day === 0 ? -6 : 1) // Adjust when day is Sunday
  return new Date(d.setDate(diff))
}

export function getWeekDays(weekStart: Date): Date[] {
  const days = []
  for (let i = 0; i < 7; i++) {
    const day = new Date(weekStart)
    day.setDate(weekStart.getDate() + i)
    days.push(day)
  }
  return days
}

export function getDayName(date: Date): string {
  return new Intl.DateTimeFormat('es-ES', { weekday: 'long' }).format(date)
}

export function getShortDayName(date: Date): string {
  return new Intl.DateTimeFormat('es-ES', { weekday: 'short' }).format(date)
}

// Storage utilities
export function getStoredValue<T>(key: string, defaultValue: T): T {
  try {
    const item = localStorage.getItem(key)
    return item ? JSON.parse(item) : defaultValue
  } catch {
    return defaultValue
  }
}

export function setStoredValue<T>(key: string, value: T): void {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // Handle storage errors silently
  }
}

export function removeStoredValue(key: string): void {
  try {
    localStorage.removeItem(key)
  } catch {
    // Handle storage errors silently
  }
}

// Validation utilities
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

export function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

export function pluralize(count: number, singular: string, plural?: string): string {
  if (count === 1) return `${count} ${singular}`
  return `${count} ${plural || singular + 's'}`
}

// API utilities
export function createQueryString(params: Record<string, any>): string {
  const searchParams = new URLSearchParams()
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      if (Array.isArray(value)) {
        value.forEach(item => searchParams.append(key, item.toString()))
      } else {
        searchParams.set(key, value.toString())
      }
    }
  })
  
  const queryString = searchParams.toString()
  return queryString ? `?${queryString}` : ''
}

// Error handling
export function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const ax = error as AxiosError<any>
    if (ax.response) {
      const status = ax.response.status
      const statusText = ax.response.statusText
      const msg = (ax.response.data && (ax.response.data.message || ax.response.data.error)) || ax.message
      return `Error API (${status} ${statusText}): ${msg}`
    }
    if (ax.request) {
      return `Error de red: no hay respuesta del servidor. Revisa ngrok/Vite y el backend.`
    }
    return `Error de petición: ${ax.message}`
  }
  if (error instanceof Error) {
    return error.message
  }
  if (typeof error === 'string') {
    return error
  }
  return 'Ha ocurrido un error inesperado'
}

export function getErrorDebug(error: unknown): string | null {
  if (!axios.isAxiosError(error)) return null
  const ax = error as AxiosError<any>
  const method = (ax.config?.method || '').toString().toUpperCase()
  const baseURL = ax.config?.baseURL || ''
  const url = ax.config?.url || ''
  const code = ax.code || ''
  const headers = ax.config?.headers ? JSON.stringify(ax.config.headers) : ''
  const data = ax.config?.data ? (typeof ax.config.data === 'string' ? ax.config.data : JSON.stringify(ax.config.data)) : ''
  const respData = ax.response?.data ? (typeof ax.response.data === 'string' ? ax.response.data : JSON.stringify(ax.response.data)) : ''
  const status = ax.response?.status
  const statusText = ax.response?.statusText
  return [
    `REQUEST: ${method} ${baseURL}${url}`,
    code ? `CODE: ${code}` : '',
    headers ? `REQ HEADERS: ${headers}` : '',
    data ? `REQ BODY: ${data}` : '',
    status ? `STATUS: ${status} ${statusText || ''}` : '',
    respData ? `RESP BODY: ${respData}` : '',
  ].filter(Boolean).join('\n')
}
